import type { ColumnDef } from '@tanstack/react-table';

import { Skeleton } from '@connected/ui/components/skeleton';
import { cn } from '@connected/ui/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table';

import type { DistributionBoard } from '../utils/distribution-board-list-types';

import { DistributionBoardActions } from './distribution-board-actions';

export type DistributionBoardTableProps = {
    electricalProjectVersionId: string;
    distributionBoards: DistributionBoard[];
    isLoading: boolean;
    className?: string;
    search?: Record<string, unknown>;
};

function formatDescription(board: DistributionBoard): string | null {
    const parts = [board.FloorNumber, board.SpaceNumber].filter(Boolean);
    if (parts.length === 0)
        return null;
    return parts.join(' – ');
}

export function DistributionBoardTable({
    electricalProjectVersionId,
    distributionBoards,
    isLoading,
    className,
    search,
}: DistributionBoardTableProps) {
    const { t } = useTranslation('re-inspections');
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<DistributionBoard>[]>(() => ([
        {
            accessorKey: 'Name',
            header: 'Naam',
            cell: ({ row }) => {
                const description = formatDescription(row.original);
                return (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-semibold">
                            {row.original.Identifier || 'Naamloze verdeelinrichting'}
                        </span>
                        {description && (
                            <span className="text-muted-foreground text-sm">
                                {description}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'Type',
            header: 'Type',
            cell: ({ row }) => (
                <span className="text-muted-foreground">
                    {t(`distribution-box.information.form.kind.${row.original.Kind ?? 'Unknown'}`)}
                </span>
            ),
        },
        {
            accessorKey: 'IsPVInstallation',
            header: t('distribution-box.information.form.isPVInstallation.column'),
            cell: ({ row }) => (
                row.original.IsPVInstallation
                    ? <Check className="text-primary h-4 w-4" />
                    : <span className="text-muted-foreground">-</span>
            ),
        },
        {
            accessorKey: 'Actions',
            header: '',
            cell: ({ row }) => (
                <div className="flex flex-row justify-end">
                    <DistributionBoardActions
                        distributionBoard={row.original}
                        electricalProjectVersionId={electricalProjectVersionId}
                    />
                </div>
            ),
        },
    ]), [electricalProjectVersionId, t]);

    const tableColumns = useMemo(
        () =>
            isLoading
                ? columns.map(column => ({
                        ...column,
                        cell: () => (
                            <Skeleton className="h-8 w-full" />
                        ),
                    }))
                : columns,
        [columns, isLoading],
    );

    if (distributionBoards.length === 0 && !isLoading) {
        return (
            <p className="text-muted-foreground text-center py-8 italic">
                {t('installation.layout.navigation.distribution-boxes.empty', 'Geen verdeelinrichtingen')}
            </p>
        );
    }

    return (
        <DataTable
            className={cn('bg-background-primary', className)}
            columns={tableColumns}
            data={isLoading ? [{} as DistributionBoard, {} as DistributionBoard, {} as DistributionBoard] : distributionBoards}
            rowProps={row => ({
                className: 'cursor-pointer hover:bg-accent/50',
                onClick: () => {
                    if (!isLoading && row.original.ElectricalProjectDistributionBoardId) {
                        navigate({
                            to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/information',
                            params: {
                                projectVersionId: electricalProjectVersionId,
                                distributionboardId: row.original.ElectricalProjectDistributionBoardId,
                            },
                            search: search as Record<string, string>,
                        });
                    }
                },
            })}
            cellClassName={cell => cn({
                // Name column: flexible width, takes remaining space
                'min-w-0': cell.column.id === 'Name',
                // Type column: fixed width
                'w-40 min-w-40': cell.column.id === 'Type',
                // PV Installation column: fixed width
                'w-12 min-w-12': cell.column.id === 'IsPVInstallation',
                // Actions column: minimum content width
                'w-16 min-w-16': cell.column.id === 'Actions',
            })}
        />
    );
}
